import { message } from 'antd'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { RautReportIndication } from 'features/indicationTriage'
import { PDFAssetDrawings } from 'features/reporting/pdfGeneration/types/pdfTypes'
import { Report } from 'features/reporting/types'
import { RautFormDataState } from 'pages/reporting/reports/raut/rautReportData'
import { usePublishRevisionHandler } from 'pages/reporting/shared/hooks/usePublishRevisionHandler'
import { useSignoffHandler } from 'pages/reporting/shared/hooks/useSignoffHandler'
import { useCallback, useState } from 'react'
import { SignatureData } from 'types'

interface UseReportSubmissionProps {
  metadataEntryId: string
  formData: RautFormDataState
  onUpdateFormData: (newData: Partial<RautFormDataState>) => void
  onPublishRevision: () => Promise<void>
  flatIndications: RautReportIndication[]
  screenshotUrls: Map<string, string>
  assetDrawings?: ReadonlyMap<string, PDFAssetDrawings>
  currentRevisionNumber: number
  pdfFilename: string
  report: Report
  getSignedUrlForPath: (path: string) => string | undefined
  publishLatestDraftPDF: (pdfPath?: string) => Promise<void>
}

type ErrorWithMessage = {
  message: string
}

function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Record<string, unknown>).message === 'string'
  )
}

function toErrorWithMessage(maybeError: unknown): ErrorWithMessage {
  if (isErrorWithMessage(maybeError)) return maybeError

  try {
    return new Error(
      typeof maybeError === 'string' ? maybeError : 'An unknown error occurred'
    )
  } catch {
    return new Error('An unknown error occurred')
  }
}

interface UseReportSubmissionResult {
  isSubmitting: boolean
  handleSubmitReport: () => Promise<void>
  handleSignoff: (signatureData: SignatureData) => Promise<void>
  handlePublishRevision: () => Promise<void>
}

/**
 * Hook to manage report submission operations
 */
export const useReportSubmission = ({
  metadataEntryId,
  formData,
  onUpdateFormData,
  onPublishRevision,
  flatIndications,
  screenshotUrls,
  assetDrawings,
  currentRevisionNumber,
  pdfFilename,
  publishLatestDraftPDF,
}: UseReportSubmissionProps): UseReportSubmissionResult => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { rolodexPatch } = useRolodexEdit()

  /**
   * Verify all required assets are loaded
   */
  const verifyAssets = useCallback(async () => {
    const verifyImage = async (url: string, description: string) => {
      if (!url) {
        console.error(`No URL available for ${description}`)
        return false
      }

      return new Promise<boolean>((resolve) => {
        const img = new Image()
        img.onload = () => resolve(true)
        img.onerror = () => {
          console.error(`Failed to load ${description}:`, url)
          resolve(false)
        }
        img.src = url
      })
    }

    try {
      message.loading({
        content: 'Verifying report assets...',
        key: 'assets',
        duration: 0,
      })

      const failedAssets: string[] = []
      const missingUrls: string[] = []

      // Verify asset drawings
      if (assetDrawings) {
        message.loading({
          content: 'Verifying asset drawings...',
          key: 'assets',
          duration: 0,
        })

        for (const [assetKey, drawings] of assetDrawings) {
          for (const [drawingType, drawingArray] of Object.entries(drawings)) {
            if (!drawingArray) continue

            for (const drawing of drawingArray) {
              if (!drawing?.signedUrl) {
                missingUrls.push(`Asset drawing ${assetKey} - ${drawingType}`)
                continue
              }

              const success = await verifyImage(
                drawing.signedUrl,
                `asset drawing ${assetKey} - ${drawingType}`
              )
              if (!success) {
                failedAssets.push(`Asset drawing ${assetKey} - ${drawingType}`)
              }

              const expectsScanPlan =
                drawingType === 'chunks' ||
                ('scanPlanPath' in drawing && drawing.scanPlanPath)
              if (expectsScanPlan) {
                const scanPlanSignedUrl =
                  'scanPlanSignedUrl' in drawing
                    ? drawing.scanPlanSignedUrl
                    : undefined
                if (!scanPlanSignedUrl) {
                  missingUrls.push(
                    `Asset drawing scan plan ${assetKey} - ${drawingType}`
                  )
                } else {
                  const scanPlanSuccess = await verifyImage(
                    scanPlanSignedUrl,
                    `asset drawing scan plan ${assetKey} - ${drawingType}`
                  )
                  if (!scanPlanSuccess) {
                    failedAssets.push(
                      `Asset drawing scan plan ${assetKey} - ${drawingType}`
                    )
                  }
                }
              }

              if ('legendSignedUrl' in drawing && drawing.legendPath) {
                if (!drawing.legendSignedUrl) {
                  missingUrls.push(
                    `Asset drawing legend ${assetKey} - ${drawingType}`
                  )
                  continue
                }

                const legendSuccess = await verifyImage(
                  drawing.legendSignedUrl,
                  `asset drawing legend ${assetKey} - ${drawingType}`
                )
                if (!legendSuccess) {
                  failedAssets.push(
                    `Asset drawing legend ${assetKey} - ${drawingType}`
                  )
                }
              }
            }
          }
        }
      }

      // Verify indication screenshots
      if (flatIndications.length > 0) {
        message.loading({
          content: 'Verifying indication screenshots...',
          key: 'assets',
          duration: 0,
        })

        for (const indication of flatIndications) {
          const url = screenshotUrls.get(indication.id)
          if (!url) {
            missingUrls.push(`Indication screenshot ${indication.id}`)
            continue
          }

          const success = await verifyImage(url, `indication ${indication.id}`)
          if (!success) {
            failedAssets.push(`Indication screenshot ${indication.id}`)
          }
        }
      }

      // Only throw errors if we have actual failures
      if (missingUrls.length > 0) {
        throw new Error(`Missing signed URLs for: ${missingUrls.join(', ')}`)
      }

      if (failedAssets.length > 0) {
        throw new Error(`Failed to verify assets: ${failedAssets.join(', ')}`)
      }

      message.success({
        content: 'All report assets verified successfully',
        key: 'assets',
      })
      return true
    } catch (maybeError: unknown) {
      const error = toErrorWithMessage(maybeError)
      console.error('Asset verification failed:', error)
      message.error({
        content: error.message || 'Failed to verify assets',
        key: 'assets',
      })
      return false
    }
  }, [assetDrawings, flatIndications, screenshotUrls])

  /**
   * Handle full report submission
   */
  const handleSubmitReport = useCallback(async () => {
    if (!metadataEntryId) {
      message.error('Metadata entry not yet created')
      return
    }

    try {
      setIsSubmitting(true)

      const reportData = {
        section_info: {
          main_info: formData.mainInfo,
          cover_page: formData.coverPageData,
          summary_page: formData.summaryPageData,
          calibration_page: formData.calibrationData,
          client_provided_images: formData.clientProvidedImagesData,
          asset_drawings: formData.asset_drawings,
        },
      }

      // Update Rolodex entry
      await rolodexPatch([
        {
          id: metadataEntryId,
          data: reportData,
        },
      ])

      message.success('Full report data submitted successfully!')
    } catch (error) {
      console.error('Failed to submit report:', error)
      message.error('Failed to submit report')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }, [metadataEntryId, formData, rolodexPatch])

  const generatePdfPath = useCallback(
    (timestamp: string) => `reportPDF/${pdfFilename}-${timestamp}.pdf`,
    [pdfFilename]
  )

  const { handlePublishRevision } = usePublishRevisionHandler({
    metadataEntryId,
    formData,
    currentRevisionNumber,
    publishLatestDraftPDF,
    publishRevision: onPublishRevision,
    setIsSubmitting,
    generatePdfPath,
    beforePublish: verifyAssets,
  })

  const { handleSignoff } = useSignoffHandler({
    metadataEntryId,
    formData,
    updateFormData: onUpdateFormData,
    setIsSubmitting,
    handlePublishRevision,
  })

  return {
    isSubmitting,
    handleSubmitReport,
    handleSignoff,
    handlePublishRevision,
  }
}

// Type guard for section keys
export const isSectionKey = (key: string): key is keyof RautFormDataState => {
  const validKeys: Array<keyof RautFormDataState> = [
    'mainInfo',
    'coverPageData',
    'summaryPageData',
    'calibrationData',
    'clientProvidedImagesData',
    'asset_drawings',
    'signoff',
    'revisionMetadata',
  ]
  return validKeys.includes(key as keyof RautFormDataState)
}
